import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../features/auth/contexts/AuthContext';
import SignupLoginModal from '../../../features/auth/components/SignupLoginModal/SignupLoginModal';
import './SaveButton.css';

function SaveButton({
  tasks = [],
  currentFileId = null,
  currentFileName = null,
  hasUnsavedChanges = false,
  hasRestoredDraft = false,
  alwaysVisible = false,
  loggedOutWorkspaceSnapshot = null,
}) {
  const { isLoggedIn } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveRequiredMessage, setSaveRequiredMessage] = useState('');

  const workspaceTaskCount = useMemo(() => {
    if (loggedOutWorkspaceSnapshot?.tasksByFileId) {
      return Object.values(loggedOutWorkspaceSnapshot.tasksByFileId).reduce((sum, listTasks) => {
        return sum + (Array.isArray(listTasks) ? listTasks.length : 0);
      }, 0);
    }
    if (!currentFileId) return 0;
    return tasks.filter((t) => t.file_id === currentFileId).length;
  }, [loggedOutWorkspaceSnapshot, tasks, currentFileId]);

  // Check if Save is available: logged-out, not preset, ≥2 tasks, and (unsaved changes or restored draft)
  const canSave = useMemo(() => {
    if (isLoggedIn) return false;
    if (currentFileId === 'preset' || (currentFileId && String(currentFileId).startsWith('preset:'))) return false;
    if (workspaceTaskCount < 2) return false;
    const hasMultipleWorkspaceLists = Array.isArray(loggedOutWorkspaceSnapshot?.files) && loggedOutWorkspaceSnapshot.files.length > 1;
    return hasUnsavedChanges || hasMultipleWorkspaceLists || (hasRestoredDraft && currentFileId === 'default');
  }, [isLoggedIn, hasUnsavedChanges, hasRestoredDraft, currentFileId, workspaceTaskCount, loggedOutWorkspaceSnapshot]);

  const shouldShow = alwaysVisible ? true : canSave;

  const handleSave = () => {
    if (!canSave) return;
    setSaveRequiredMessage('');
    setIsModalOpen(true);
  };

  useEffect(() => {
    const handleSaveRequired = (event) => {
      const message = event?.detail?.message || 'Save to continue.';
      setSaveRequiredMessage(message);
      setIsModalOpen(true);
    };
    window.addEventListener('taskdown:save_required', handleSaveRequired);
    return () => window.removeEventListener('taskdown:save_required', handleSaveRequired);
  }, []);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={`save-button text-non-selectable${alwaysVisible ? ' save-button-in-toolbar' : ''}${!canSave && alwaysVisible ? ' save-button-muted' : ''}`}
        onClick={handleSave}
        disabled={alwaysVisible && !canSave}
        data-save-button
      >
        <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path fill="currentColor" d="M21 7v12q0 .825-.587 1.413T19 21H5q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h12zm-2 .85L16.15 5H5v14h14zM12 18q1.25 0 2.125-.875T15 15t-.875-2.125T12 12t-2.125.875T9 15t.875 2.125T12 18m-6-8h9V6H6zM5 7.85V19V5z"/>
        </svg>
        Save
      </button>
      <SignupLoginModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tasks={tasks}
        currentFileId={currentFileId}
        currentFileName={currentFileName}
        loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
        saveRequiredMessage={saveRequiredMessage}
        source="save"
      />
    </>
  );
}

export default SaveButton;

